import { useTranslation } from "react-i18next";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Checkbox } from "@libs/zapi-ui/ds/components/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@libs/zapi-ui/ds/components/popover";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { BODY_2, BODY_3, LABEL_2 } from "./_tokens";

type TTwinFiltersProps = {
  groups: { id: string; label: string; count: number }[];
  hiddenGroups: Set<string>;
  changedOnly: boolean;
  onToggleGroup: (id: string, shown: boolean) => void;
  onChangedOnly: (on: boolean) => void;
  onReset: () => void;
};

const ROW = cn(
  BODY_2,
  "flex min-h-8 cursor-pointer items-center gap-2 rounded-lg px-2 py-1 text-innoveraZinc-700 hover:bg-innoveraZinc-50"
);
const CHECKBOX = "[&_svg]:h-3 [&_svg]:w-3";

export function TwinFilters({
  groups,
  hiddenGroups,
  changedOnly,
  onToggleGroup,
  onChangedOnly,
  onReset
}: TTwinFiltersProps) {
  const { t } = useTranslation("projectHome");
  const active = hiddenGroups.size + (changedOnly ? 1 : 0);

  return (
    <Popover>
      <PopoverTrigger
        render={
          <Button
            variant="outline"
            data-testid="rqa-sim-configure-filters"
            className={cn("rounded-full", active > 0 && "border-greyscale-900")}
          />
        }
      >
        {t("rqaSim.filters")}
        {active > 0 && (
          <span className="flex items-center justify-center rounded-full bg-innoveraZinc-900 px-1.5 py-[3px] text-[12px] font-medium leading-[11px] text-white">
            {active}
          </span>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={6}
        className="w-[270px] gap-0 rounded-2xl border-0 bg-popover p-0 shadow-lg ring-1 ring-foreground/10"
        data-testid="rqa-sim-configure-filters-popover"
      >
        <div className="flex h-12 items-center justify-between border-b border-innoveraZinc-100 px-3">
          <span className={LABEL_2}>{t("rqaSim.filters")}</span>
          <button
            type="button"
            disabled={active === 0}
            onClick={onReset}
            data-testid="rqa-sim-configure-filters-reset"
            className={cn(BODY_2, "text-innoveraZinc-700 disabled:cursor-not-allowed disabled:text-innoveraZinc-400")}
          >
            {t("rqaSim.resetShort")}
          </button>
        </div>
        <div className="max-h-[420px] overflow-y-auto p-2">
          <label className={ROW}>
            <Checkbox
              checked={changedOnly}
              onCheckedChange={onChangedOnly}
              className={CHECKBOX}
              data-testid="rqa-sim-configure-filter-changed"
            />
            <span className="min-w-0 flex-1 truncate">{t("rqaSim.changedOnly")}</span>
          </label>
          {groups.length > 1 && (
            <>
              <div className={cn(BODY_3, "px-2 pb-1 pt-3 text-innoveraZinc-600")}>{t("rqaSim.filterGroups")}</div>
              {groups.map((group) => (
                <label key={group.id} className={ROW}>
                  <Checkbox
                    checked={!hiddenGroups.has(group.id)}
                    onCheckedChange={(checked) => onToggleGroup(group.id, checked)}
                    className={CHECKBOX}
                    data-testid={`rqa-sim-configure-filter-group-${group.id}`}
                  />
                  <span className="min-w-0 flex-1 wrap-break-word">{group.label}</span>
                  <span className={cn(BODY_3, "text-innoveraZinc-500")}>{group.count}</span>
                </label>
              ))}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
